import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { CharacterFeature, CharacterWithStats } from '@shared/types'
import {
  ALIGNMENTS, BACKGROUNDS, CLASSES, CLASS_KEYS, SPECIES,
  type Ability, type ClassKey, type ProficiencyLevel, type SkillKey,
} from '@shared/rules/constants'
import { deriveStats, formatModifier, xpToNextLevel } from '@shared/rules/calc'
import { ApiError, api } from '@/lib/api'
import type { Roll } from '@shared/types'
import { DiceRoller } from '@/components/dice/DiceRoller'
import { RollResultCard } from '@/components/dice/RollResultCard'
import { characterToSheetData } from '@/lib/pdf/fromCharacter'
import { downloadCharacterSheet } from '@/lib/pdf/fillSheet'
import { useAsync } from '@/lib/useAsync'
import { calcInputOf, editablePayload } from '@/lib/sheet'
import {
  Badge, Button, ErrorNote, Field, Input, Modal, NumberField, PageLoader, Panel, SectionHeading, Select, Textarea,
} from '@/components/ui'
import { AbilityColumn, SavesPanel, SkillsPanel, VitalsRow } from '@/components/sheet/StatBlocks'
import { HealthPanel } from '@/components/sheet/HealthPanel'
import { SpellcastingPanel } from '@/components/sheet/SpellcastingPanel'
import { SpellsPanel } from '@/components/sheet/SpellsPanel'
import { RestPanel } from '@/components/sheet/RestPanel'
import {
  AttacksPanel, EquipmentPanel, ProficienciesPanel, TraitsPanel,
} from '@/components/sheet/GearPanels'

export default function CharacterSheet() {
  const { characterId = '' } = useParams()
  const navigate = useNavigate()
  const { data, error, loading, setData } = useAsync(() => api.getCharacter(characterId), [characterId])

  const [draft, setDraft] = useState<CharacterWithStats | null>(null)
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [levelUpOpen, setLevelUpOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [lastRoll, setLastRoll] = useState<Roll | null>(null)

  const shown = draft ?? data?.character ?? null

  // While editing, recompute locally so every bonus updates as you type.
  const derived = useMemo(
    () => (draft ? deriveStats(calcInputOf(draft)) : data?.character.derived ?? null),
    [draft, data?.character.derived],
  )

  if (loading) return <PageLoader />
  if (error || !data || !shown || !derived) return <ErrorNote>{error ?? 'Character not found'}</ErrorNote>

  const { canEdit, myRole } = data
  const classDef = CLASSES[shown.class as ClassKey]
  const editing = draft !== null

  function replace(character: CharacterWithStats) {
    setData({ ...data!, character })
  }

  async function patchLive(patch: Partial<CharacterWithStats>) {
    const optimistic = { ...data!.character, ...patch }
    replace(optimistic)
    setActionError(null)
    try {
      const res = await api.updateCharacter(characterId, patch as Record<string, unknown>)
      replace(res.character)
    } catch (err) {
      replace(data!.character) // roll the optimistic update back
      setActionError(err instanceof ApiError ? err.message : 'Could not save')
    }
  }

  async function saveDraft() {
    if (!draft) return
    setSaving(true)
    setActionError(null)
    try {
      const res = await api.updateCharacter(characterId, editablePayload(draft))
      replace(res.character)
      setDraft(null)
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not save the sheet')
    } finally {
      setSaving(false)
    }
  }

  const update = (patch: Partial<CharacterWithStats>) => setDraft({ ...shown, ...patch })

  /** Rolls from the sheet are attributed to this character in the DM's feed. */
  async function rollFor(formula: string, context: string) {
    setActionError(null)
    try {
      const res = await api.createRoll(shown!.campaignId, {
        formula, context, characterId: shown!.id,
      })
      setLastRoll(res.roll)
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not roll')
    }
  }

  // Guests are read-only, and edit mode repurposes the same rows for toggling.
  const rollHandler = myRole !== 'guest' && !editing ? rollFor : undefined
  const xp = xpToNextLevel(shown.xp)

  return (
    <div className="space-y-5 pb-10">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link to={`/campaigns/${shown.campaignId}`} className="text-sm text-ink-500 transition-colors hover:text-ink-300">
            ← Back to the campaign
          </Link>

          {editing ? (
            <input
              value={shown.name}
              onChange={(e) => update({ name: e.target.value })}
              aria-label="Character name"
              className="mt-2 w-full max-w-md rounded-lg border border-ink-600 bg-ink-900 px-3 py-1.5 font-display text-2xl text-ink-100"
            />
          ) : (
            <h1 className="mt-3 text-display">{shown.name}</h1>
          )}

          <p className="mt-1.5 text-sm text-ink-400">
            Level {shown.level} {classDef?.name ?? shown.class ?? 'Adventurer'}
            {shown.subclass && ` (${shown.subclass})`}
            {shown.species && ` · ${shown.species}`}
            {shown.background && ` · ${shown.background}`}
            {shown.alignment && ` · ${shown.alignment}`}
          </p>

          {shown.ownerName && !editing && (
            <p className="mt-1 text-xs text-ink-500">Played by {shown.ownerName}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {myRole === 'guest' && <Badge>Read only</Badge>}
          {!editing && (
            <Button
              size="sm"
              busy={downloading}
              onClick={async () => {
                setDownloading(true)
                setActionError(null)
                try {
                  await downloadCharacterSheet(characterToSheetData(shown!))
                } catch (err) {
                  setActionError(err instanceof Error ? err.message : 'Could not build the PDF')
                } finally {
                  setDownloading(false)
                }
              }}
            >
              Download sheet
            </Button>
          )}
          {canEdit && !editing && (
            <>
              <Button size="sm" onClick={() => setDraft({ ...shown })}>Edit sheet</Button>
              <Button size="sm" variant="primary" disabled={shown.level >= 20} onClick={() => setLevelUpOpen(true)}>
                Level up
              </Button>
            </>
          )}
          {editing && (
            <>
              <Button size="sm" variant="ghost" onClick={() => { setDraft(null); setActionError(null) }}>Cancel</Button>
              <Button size="sm" variant="primary" busy={saving} onClick={saveDraft}>Save sheet</Button>
            </>
          )}
        </div>
      </header>

      {actionError && <ErrorNote>{actionError}</ErrorNote>}

      <VitalsRow
        derived={derived}
        speed={formatSpeed(shown.speed, shown.otherSpeeds)}
        hitDiceTotal={shown.hitDiceTotal}
        hitDiceSpent={shown.hitDiceSpent}
        hitDie={derived.hitDie}
      />

      {editing && <IdentityPanel character={shown} onChange={update} />}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5">
          <AbilityColumn
            scores={shown.scores}
            derived={derived}
            onRoll={rollHandler}
            editing={editing}
            onChange={(ability: Ability, value) => update({ scores: { ...shown.scores, [ability]: value } })}
          />
          <SavesPanel
            derived={derived}
            saveProficiencies={shown.saveProficiencies}
            onRoll={rollHandler}
            editing={editing}
            onToggle={(ability) =>
              update({
                saveProficiencies: shown.saveProficiencies.includes(ability)
                  ? shown.saveProficiencies.filter((a) => a !== ability)
                  : [...shown.saveProficiencies, ability],
              })
            }
          />
        </div>

        <div className="space-y-5 lg:col-span-2">
          <HealthPanel character={shown} canEdit={canEdit && !editing} onPatch={patchLive} />

          {!editing && <AttacksPanel character={shown} onRoll={rollHandler} />}

          <SkillsPanel
            derived={derived}
            skillProficiencies={shown.skillProficiencies}
            onRoll={rollHandler}
            editing={editing}
            onChange={(skill: SkillKey, level: ProficiencyLevel | null) => {
              const next = { ...shown.skillProficiencies }
              if (level) next[skill] = level
              else delete next[skill]
              update({ skillProficiencies: next })
            }}
          />

          {!editing && (
            <RestPanel
              character={shown}
              canEdit={canEdit}
              onUpdated={replace}
              onError={setActionError}
            />
          )}

          {derived.spellcasting && (
            <SpellcastingPanel
              spellcasting={derived.spellcasting}
              slotsUsed={shown.slotsUsed}
              concentratingOn={shown.concentratingOn}
              canEdit={canEdit && !editing}
              onSlotsChange={(slotsUsed) => patchLive({ slotsUsed })}
              onConcentrationChange={(concentratingOn) => patchLive({ concentratingOn })}
            />
          )}

          {!editing && (
            <SpellsPanel
              spells={shown.spells}
              className={shown.class}
              canEdit={canEdit}
              concentratingOn={shown.concentratingOn}
              onChange={(spells) => patchLive({ spells })}
              onConcentrate={(spellId) => patchLive({ concentratingOn: spellId })}
            />
          )}

          <FeaturesPanel
            features={shown.features}
            editing={editing}
            onChange={(features) => update({ features })}
          />

          {!editing && (
            <>
              <TraitsPanel title="Species traits" entries={shown.speciesTraits} />
              <TraitsPanel title="Feats" entries={shown.feats} />
              <EquipmentPanel character={shown} />
              <ProficienciesPanel character={shown} />
              {shown.appearance && (
                <Panel className="p-4">
                  <SectionHeading>Appearance</SectionHeading>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-300">{shown.appearance}</p>
                </Panel>
              )}
              {(shown.personality || shown.backstory) && (
                <Panel className="space-y-3 p-4">
                  <SectionHeading>Backstory &amp; personality</SectionHeading>
                  {shown.personality && (
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-300">{shown.personality}</p>
                  )}
                  {shown.backstory && (
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-300">{shown.backstory}</p>
                  )}
                </Panel>
              )}
            </>
          )}

          <Panel className="p-4">
            <SectionHeading
              action={
                xp.next !== null && (
                  <span className="font-mono text-[11px] text-ink-500">
                    {shown.xp.toLocaleString()} / {xp.next.toLocaleString()} XP
                  </span>
                )
              }
            >
              Notes
            </SectionHeading>
            {editing ? (
              <Textarea
                value={shown.notes}
                onChange={(e) => update({ notes: e.target.value })}
                placeholder="Backstory, bonds, the debt you owe the harbourmaster…"
                className="min-h-40"
              />
            ) : shown.notes ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-300">{shown.notes}</p>
            ) : (
              <p className="text-sm text-ink-500">Nothing written down yet.</p>
            )}
          </Panel>

          {canEdit && !editing && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setDeleteOpen(true)} className="text-ink-500 hover:text-blood-500">
                Delete character
              </Button>
            </div>
          )}
        </div>
      </div>

      {lastRoll && (
        <div className="fixed bottom-24 left-5 z-40 w-[min(20rem,calc(100vw-2.5rem))]">
          <div className="panel panel-lit p-2">
            <RollResultCard roll={lastRoll} showWho={false} />
            <button
              type="button"
              onClick={() => setLastRoll(null)}
              className="mt-1 w-full rounded-lg py-1 text-[11px] uppercase tracking-wider text-ink-500 transition-colors hover:text-ink-300"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {myRole !== 'guest' && (
        <DiceRoller
          campaignId={shown.campaignId}
          characterId={shown.id}
          canRollPrivately={myRole === 'dm'}
          onRolled={setLastRoll}
        />
      )}

      <LevelUpModal
        open={levelUpOpen}
        character={shown}
        onClose={() => setLevelUpOpen(false)}
        onDone={replace}
      />

      <Modal open={deleteOpen} title={`Delete ${shown.name}?`} onClose={() => setDeleteOpen(false)}>
        <p className="text-sm text-ink-400">
          This removes the sheet from the campaign permanently. There is no undo.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Keep it</Button>
          <Button
            variant="danger"
            onClick={async () => {
              try {
                await api.deleteCharacter(characterId)
                navigate(`/campaigns/${shown.campaignId}`, { replace: true })
              } catch (err) {
                setActionError(err instanceof ApiError ? err.message : 'Could not delete')
                setDeleteOpen(false)
              }
            }}
          >
            Delete permanently
          </Button>
        </div>
      </Modal>
    </div>
  )
}

/** "30 ft" on its own, or "30 ft · fly 30 ft" when a species grants more. */
function formatSpeed(walking: number, other: Record<string, number>): string {
  const extra = Object.entries(other).map(([mode, value]) => `${mode} ${value} ft`)
  return [`${walking} ft`, ...extra].join(' · ')
}

// ---------- Edit-mode identity block ----------

function IdentityPanel({
  character, onChange,
}: {
  character: CharacterWithStats
  onChange: (patch: Partial<CharacterWithStats>) => void
}) {
  return (
    <Panel className="space-y-4 p-4">
      <SectionHeading>Identity &amp; defenses</SectionHeading>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Class">
          {(id) => (
            <Select id={id} value={character.class} onChange={(e) => onChange({ class: e.target.value })}>
              <option value="">—</option>
              {CLASS_KEYS.map((key) => <option key={key} value={key}>{CLASSES[key].name}</option>)}
            </Select>
          )}
        </Field>
        <Field label="Subclass">
          {(id) => <Input id={id} value={character.subclass} maxLength={60} onChange={(e) => onChange({ subclass: e.target.value })} />}
        </Field>
        <Field label="Species">
          {(id) => (
            <Select id={id} value={character.species} onChange={(e) => onChange({ species: e.target.value })}>
              <option value="">—</option>
              {SPECIES.map((value) => <option key={value} value={value}>{value}</option>)}
            </Select>
          )}
        </Field>
        <Field label="Background">
          {(id) => (
            <Select id={id} value={character.background} onChange={(e) => onChange({ background: e.target.value })}>
              <option value="">—</option>
              {BACKGROUNDS.map((value) => <option key={value} value={value}>{value}</option>)}
            </Select>
          )}
        </Field>
        <Field label="Alignment">
          {(id) => (
            <Select id={id} value={character.alignment} onChange={(e) => onChange({ alignment: e.target.value })}>
              <option value="">—</option>
              {ALIGNMENTS.map((value) => <option key={value} value={value}>{value}</option>)}
            </Select>
          )}
        </Field>
        <Field label="Max HP">
          {(id) => (
            <NumberField id={id} min={1} max={999} fallback={1} value={character.hpMax}
              onChange={(hpMax) => onChange({ hpMax })} className="w-full" />
          )}
        </Field>
        <Field label="Speed (ft)">
          {(id) => (
            <NumberField id={id} min={0} max={200} step={5} fallback={30} value={character.speed}
              onChange={(speed) => onChange({ speed })} className="w-full" />
          )}
        </Field>
        <Field label="Initiative bonus" hint="On top of your Dexterity modifier.">
          {(id) => (
            <NumberField id={id} min={-10} max={20} fallback={0} value={character.initiativeBonus}
              onChange={(initiativeBonus) => onChange({ initiativeBonus })} className="w-full" />
          )}
        </Field>
      </div>

      <div className="flex flex-wrap items-end gap-4 border-t border-ink-700/60 pt-4">
        <label className="flex items-center gap-2 text-sm text-ink-300">
          <input
            type="checkbox"
            checked={character.acIsManual}
            onChange={(e) => onChange({ acIsManual: e.target.checked })}
            className="size-4 accent-gold-500"
          />
          Set armor class manually
        </label>

        <Field label="Armor class">
          {(id) => (
            <NumberField id={id} min={0} max={40} fallback={10} value={character.armorClass}
              disabled={!character.acIsManual}
              onChange={(armorClass) => onChange({ armorClass })}
              className="w-24" />
          )}
        </Field>

        <Field label="Experience points">
          {(id) => (
            <NumberField id={id} min={0} max={999999} fallback={0} value={character.xp}
              onChange={(xp) => onChange({ xp })}
              className="w-32" />
          )}
        </Field>

        {!character.acIsManual && (
          <p className="text-xs text-ink-500">
            Unarmored: 10 + Dexterity ({formatModifier(Math.floor((character.scores.dex - 10) / 2))}).
          </p>
        )}
      </div>
    </Panel>
  )
}

// ---------- Features ----------

function FeaturesPanel({
  features, editing, onChange,
}: {
  features: CharacterFeature[]
  editing: boolean
  onChange: (features: CharacterFeature[]) => void
}) {
  function add() {
    onChange([...features, { id: crypto.randomUUID(), name: '', source: '', description: '' }])
  }

  function patch(index: number, patchValue: Partial<CharacterFeature>) {
    onChange(features.map((feature, i) => (i === index ? { ...feature, ...patchValue } : feature)))
  }

  return (
    <Panel className="p-4">
      <SectionHeading action={editing && <Button size="sm" onClick={add}>Add feature</Button>}>
        Features &amp; traits
      </SectionHeading>

      {features.length === 0 ? (
        <p className="text-sm text-ink-500">
          {editing
            ? 'Add class features, feats, or species traits. These become linked cards once the rules database lands.'
            : 'No features recorded yet.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={feature.id} className="rounded-xl border border-ink-700/60 bg-ink-800/50 p-3">
              {editing ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input value={feature.name} placeholder="Cunning Action" maxLength={120}
                      onChange={(e) => patch(index, { name: e.target.value })} />
                    <Input value={feature.source} placeholder="Rogue 2" maxLength={60} className="w-36"
                      onChange={(e) => patch(index, { source: e.target.value })} />
                    <Button variant="ghost" size="sm" aria-label={`Remove ${feature.name || 'feature'}`}
                      onClick={() => onChange(features.filter((_, i) => i !== index))}>
                      ✕
                    </Button>
                  </div>
                  <Textarea value={feature.description} placeholder="What it does…" maxLength={4000}
                    onChange={(e) => patch(index, { description: e.target.value })} />
                </div>
              ) : (
                <>
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display text-sm text-ink-100">{feature.name || 'Untitled feature'}</h3>
                    {feature.source && <span className="shrink-0 text-[11px] uppercase tracking-wider text-ink-500">{feature.source}</span>}
                  </div>
                  {feature.description && (
                    <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-ink-300">{feature.description}</p>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

// ---------- Level up ----------

function LevelUpModal({
  open, character, onClose, onDone,
}: {
  open: boolean
  character: CharacterWithStats
  onClose: () => void
  onDone: (character: CharacterWithStats) => void
}) {
  const [mode, setMode] = useState<'average' | 'roll' | 'manual'>('average')
  const [manual, setManual] = useState(1)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ gained: number; rolled: number | null; level: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const def = CLASSES[character.class as ClassKey]
  const conMod = Math.floor((character.scores.con - 10) / 2)

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const res = await api.levelUp(character.id, {
        hpMode: mode,
        ...(mode === 'manual' ? { hpGained: manual } : {}),
      })
      onDone(res.character)
      setResult({ gained: res.gained, rolled: res.rolled, level: res.level })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not level up')
    } finally {
      setBusy(false)
    }
  }

  function close() {
    setResult(null)
    setError(null)
    onClose()
  }

  return (
    <Modal open={open} title={result ? `Welcome to level ${result.level}` : `Level up to ${character.level + 1}`} onClose={close}>
      {result ? (
        <div className="space-y-4 text-sm text-ink-300">
          <p>
            Gained <span className="font-mono text-gold-400">+{result.gained}</span> hit points
            {result.rolled !== null && <> (rolled a <span className="font-mono">{result.rolled}</span> on the d{def?.hitDie})</>}
            {conMod !== 0 && <> including <span className="font-mono">{formatModifier(conMod)}</span> from Constitution</>}.
          </p>
          <p className="text-ink-500">
            Add any new class features on the sheet — the guided feature picker arrives with the rules database.
          </p>
          <div className="flex justify-end">
            <Button variant="primary" onClick={close}>Done</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {!def && <ErrorNote>Set a class on the sheet before levelling up.</ErrorNote>}

          <Field label="Hit points" hint={def ? `d${def.hitDie} hit die, ${formatModifier(conMod)} from Constitution` : undefined}>
            {(id) => (
              <Select id={id} value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
                <option value="average">Take the average{def ? ` (+${Math.max(1, def.hitDie / 2 + 1 + conMod)})` : ''}</option>
                <option value="roll">Roll the hit die</option>
                <option value="manual">Enter it myself</option>
              </Select>
            )}
          </Field>

          {mode === 'manual' && (
            <Field label="Hit points gained" hint="Before your Constitution modifier.">
              {(id) => (
                <NumberField id={id} min={1} max={30} fallback={1} value={manual}
                  onChange={setManual} className="w-full" />
              )}
            </Field>
          )}

          {error && <ErrorNote>{error}</ErrorNote>}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button variant="primary" busy={busy} disabled={!def} onClick={submit}>
              Level up
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
